import { useEffect, useState } from "react";
import { useNavigate, useSearchParams, Link } from "react-router-dom";
import { api, formatErr } from "@/lib/api";
import { CheckCircle, XCircle, CircleNotch, EnvelopeSimple } from "@phosphor-icons/react";

/**
 * Public callback page the applicant lands on after clicking the verification
 * link in their institution inbox. It calls GET /api/ala/email/verify?token=…
 * and then deep-links them back into the ALA wizard.
 */
export default function ParentALAVerifyEmail() {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const token = params.get("token") || "";
  const [status, setStatus] = useState("checking"); // checking | ok | error
  const [message, setMessage] = useState("");
  const [email, setEmail] = useState("");

  useEffect(() => {
    if (!token) { setStatus("error"); setMessage("Missing verification token."); return; }
    (async () => {
      try {
        const { data } = await api.get(`/ala/email/verify?token=${encodeURIComponent(token)}`);
        setStatus("ok");
        setEmail(data.email || "");
        setMessage(
          data.already_verified
            ? "This email was already verified."
            : "Your institution email has been verified."
        );
      } catch (e) {
        setStatus("error");
        setMessage(formatErr(e?.response?.data?.detail) || e.message);
      }
    })();
  }, [token]);

  return (
    <div className="min-h-screen flex items-center justify-center bg-[#F9F7F4] px-4">
      <div className="w-full max-w-md bg-white rounded-2xl border border-[#E5E3DB] p-8 text-center shadow-sm">
        <div className="w-14 h-14 mx-auto rounded-full bg-[#F0EFEA] flex items-center justify-center">
          {status === "checking"
            ? <CircleNotch size={28} className="animate-spin text-[#0A3622]" />
            : status === "ok"
            ? <CheckCircle size={32} weight="fill" className="text-[#0A3622]" />
            : <XCircle size={32} weight="fill" className="text-[#C93B3B]" />}
        </div>

        <h1 className="font-display text-2xl mt-5">
          {status === "checking" ? "Verifying your email…" : status === "ok" ? "Email verified" : "Verification failed"}
        </h1>

        <p className="text-sm text-[#4F5D4F] mt-2" data-testid="ala-verify-message">{message}</p>
        {email && status === "ok" && (
          <p className="text-xs text-[#4F5D4F] mt-1 font-mono">{email}</p>
        )}

        <div className="mt-6 space-y-2">
          {status === "ok" && (
            <button
              className="btn-primary w-full justify-center"
              onClick={() => navigate("/parent/academic-advance")}
              data-testid="ala-verify-continue"
            >
              Continue my application
            </button>
          )}
          {status === "error" && (
            <Link to="/parent/academic-advance" className="btn-primary w-full justify-center" data-testid="ala-verify-retry">
              <EnvelopeSimple size={16} weight="fill" /> Return & resend link
            </Link>
          )}
          <Link to="/" className="block text-xs text-[#4F5D4F] mt-3 hover:text-[#0A3622]">Back to home</Link>
        </div>
      </div>
    </div>
  );
}
